import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import clsx from 'clsx';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Sparkles, ArrowUp, Loader2, MessageCircle, Mic, MicOff, PhoneOff, Phone, Clock, Plus, Trash2, X } from 'lucide-react';

import { portalApi } from '../../../api/portal.js';
import { useRealtimeVoice } from '../../../hooks/useRealtimeVoice.js';
import { env } from '../../../lib/env.js';
import { ChatWidgets } from '../../../components/ai/ChatWidgets.jsx';

// LocalStorage key for the active assistant conversation, so a tab switch does
// not lose the in-progress thread (the reply is persisted server-side).
const RESUME_KEY = 'orme.portal.assistant.convo';

/**
 * Personal assistant for the logged-in owner. Two modes share one screen:
 * text chat (streamed) and a realtime voice call. Both run server-side strictly
 * scoped to this owner's own data (own profile, own animals, own visits) and the
 * catalogue of their store: the assistant can never reach another customer.
 */
export function AssistantPage() {
  const { customer } = useOutletContext();
  const [mode, setMode] = useState('chat');
  const [voiceEnabled, setVoiceEnabled] = useState(false);

  useEffect(() => {
    let alive = true;
    portalApi.assistantConfig()
      .then((cfg) => { if (alive) setVoiceEnabled(Boolean(cfg && cfg.voiceEnabled)); })
      .catch(() => { if (alive) setVoiceEnabled(false); });
    return () => { alive = false; };
  }, []);

  const name = (customer && customer.firstName) || '';

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col pt-2">
      <div className="mb-3">
        <h2 className="text-2xl font-semibold text-portal-ink">Assistente</h2>
        <p className="mt-1 text-portal-ink-muted">
          {name ? `Ciao ${name}, ` : ''}sono qui per te e per i tuoi animali. Scrivimi o parlami.
        </p>
      </div>

      {/* Mode switch */}
      <div className="mb-3 flex gap-2">
        <ModeButton active={mode === 'chat'} onClick={() => setMode('chat')} Icon={MessageCircle} label="Messaggi" />
        <ModeButton
          active={mode === 'voice'}
          onClick={() => setMode('voice')}
          Icon={Mic}
          label="Voce"
          disabled={!voiceEnabled}
        />
      </div>

      {mode === 'chat' ? <ChatMode /> : <VoiceMode enabled={voiceEnabled} />}
    </div>
  );
}

function ModeButton({ active, onClick, Icon, label, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition',
        active ? 'bg-portal-brand text-white' : 'bg-portal-card text-portal-ink-muted hover:text-portal-ink',
        disabled && 'cursor-not-allowed opacity-40'
      )}
    >
      <Icon className="h-4 w-4" strokeWidth={1.75} />
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Text chat
// ---------------------------------------------------------------------------
function ChatMode() {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState({ status: 'idle', items: [] });
  const [confirm, setConfirm] = useState({ id: null, deleting: false });
  const convoRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  // Remember the active conversation so switching the bottom-nav tab (which
  // unmounts this page) does not lose the reply: it is persisted server-side
  // and re-loaded here on return.
  const persistConvo = (id) => {
    try { if (id) localStorage.setItem(RESUME_KEY, id); else localStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
  };

  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(RESUME_KEY); } catch { /* ignore */ }
    if (stored) resume(stored);
    // Run once on mount to restore the active conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async () => {
    setHistory((h) => ({ ...h, status: 'loading' }));
    try {
      const data = await portalApi.assistantConversations();
      setHistory({ status: 'ready', items: (data && data.items) || [] });
    } catch {
      setHistory({ status: 'error', items: [] });
    }
  };

  const openHistory = () => { setShowHistory(true); loadHistory(); };

  const newConversation = () => {
    convoRef.current = null;
    persistConvo(null);
    setMessages([]);
    setError(null);
    setShowHistory(false);
  };

  const resume = async (id) => {
    setShowHistory(false);
    setError(null);
    setMessages([{ role: 'assistant', content: '', streaming: true }]);
    try {
      const data = await portalApi.assistantConversation(id);
      convoRef.current = id;
      persistConvo(id);
      setMessages((data.messages || []).map((m) => ({ role: m.role, content: m.content, widgets: m.widgets || [] })));
    } catch {
      convoRef.current = null;
      persistConvo(null);
      setMessages([]);
      setError('Non riesco ad aprire questa conversazione.');
    }
  };

  // Two-phase delete: the trash icon asks for confirmation, the modal performs it.
  const requestDelete = (id, e) => {
    e.stopPropagation();
    setConfirm({ id, deleting: false });
  };

  const confirmDelete = async () => {
    const id = confirm.id;
    if (!id) return;
    setConfirm((c) => ({ ...c, deleting: true }));
    try {
      await portalApi.assistantDeleteConversation(id);
      setHistory((h) => ({ ...h, items: h.items.filter((c) => c.id !== id) }));
      if (convoRef.current === id) newConversation();
      setConfirm({ id: null, deleting: false });
    } catch {
      setConfirm({ id: null, deleting: false });
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setError(null);
    setDraft('');
    setSending(true);
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      { role: 'assistant', content: '', streaming: true }
    ]);

    const patchLast = (patch) => setMessages((prev) => {
      const next = prev.slice();
      const last = next[next.length - 1];
      if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, ...(typeof patch === 'function' ? patch(last) : patch) };
      }
      return next;
    });

    try {
      const final = await portalApi.assistantChatStream(
        { message: text, conversationId: convoRef.current || undefined },
        {
          onEvent: (evt) => {
            if (evt.type === 'delta' && typeof evt.text === 'string') {
              patchLast((m) => ({ content: m.content + evt.text }));
            }
          }
        }
      );
      if (final && final.conversationId) { convoRef.current = final.conversationId; persistConvo(final.conversationId); }
      const widgets = final && Array.isArray(final.widgets) ? final.widgets : [];
      // Prefer the final reply when present (it is the clean, persisted answer);
      // otherwise keep whatever the deltas already accumulated.
      if (final && typeof final.reply === 'string') patchLast({ content: final.reply, widgets, streaming: false });
      else patchLast({ widgets, streaming: false });
    } catch (err) {
      // Drop the empty assistant placeholder and surface the error.
      setMessages((prev) => {
        const next = prev.slice();
        if (next.length && next[next.length - 1].role === 'assistant' && next[next.length - 1].streaming) next.pop();
        return next;
      });
      setError(err && err.statusCode === 404
        ? 'Il tuo account non è ancora collegato a una scheda cliente.'
        : 'Non riesco a rispondere in questo momento. Riprova tra poco.');
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="mb-2 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={openHistory}
          className="inline-flex items-center gap-1.5 rounded-full bg-portal-card px-3 py-1.5 text-xs font-medium text-portal-ink-muted transition hover:text-portal-ink"
        >
          <Clock className="h-3.5 w-3.5" strokeWidth={1.75} />
          Cronologia
        </button>
        <button
          type="button"
          onClick={newConversation}
          className="inline-flex items-center gap-1.5 rounded-full bg-portal-card px-3 py-1.5 text-xs font-medium text-portal-ink-muted transition hover:text-portal-ink"
        >
          <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
          Nuova
        </button>
      </div>

      {showHistory && (
        <HistoryPanel
          history={history}
          onClose={() => setShowHistory(false)}
          onSelect={resume}
          onDelete={requestDelete}
          activeId={convoRef.current}
        />
      )}

      <ConfirmModal
        open={Boolean(confirm.id)}
        deleting={confirm.deleting}
        onCancel={() => { if (!confirm.deleting) setConfirm({ id: null, deleting: false }); }}
        onConfirm={confirmDelete}
      />

      <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-3">
        {messages.length === 0 && (
          <div className="rounded-portal bg-portal-card p-8 text-center shadow-portal">
            <Sparkles className="mx-auto h-9 w-9 text-portal-sage" strokeWidth={1.5} />
            <p className="mt-3 text-portal-ink-muted">
              Chiedimi un consiglio sull'alimentazione, sulle visite o sui prodotti adatti al tuo animale.
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i}>
            <Bubble role={m.role} content={m.content} streaming={m.streaming} />
            {m.role === 'assistant' && !m.streaming && m.widgets && m.widgets.length > 0 && (
              <div className="mt-1 pl-1 text-portal-ink">
                <ChatWidgets widgets={m.widgets} />
              </div>
            )}
          </div>
        ))}
      </div>

      {error && <p className="pb-2 text-center text-sm text-portal-brand">{error}</p>}

      <div className="rounded-2xl border border-portal-border bg-portal-card p-2 shadow-portal">
        <textarea
          rows={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Scrivi un messaggio..."
          style={{ outline: 'none' }}
          className="max-h-40 w-full resize-none bg-transparent px-3 py-2 text-sm text-portal-ink placeholder:text-portal-ink-faint [outline:none] focus:[outline:none] focus-visible:[outline:none]"
        />
        <div className="flex items-center justify-between px-1 pt-1">
          <span className="text-xs text-portal-ink-faint">Invio per inviare</span>
          <button
            type="button"
            onClick={send}
            disabled={sending || !draft.trim()}
            aria-label="Invia"
            className={clsx(
              'inline-flex h-9 w-9 items-center justify-center rounded-full bg-portal-brand text-white transition hover:bg-portal-brand-hover',
              (sending || !draft.trim()) && 'cursor-not-allowed opacity-50'
            )}
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} /> : <ArrowUp className="h-4 w-4" strokeWidth={2.25} />}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, deleting, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-5"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Annulla"
        onClick={onCancel}
        className="absolute inset-0 cursor-default bg-portal-ink/30 backdrop-blur-sm [outline:none]"
      />
      <div className="relative w-full max-w-sm rounded-portal bg-portal-card p-6 text-center shadow-portal">
        <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-portal-brand/10 text-portal-brand">
          <Trash2 className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <h3 className="mt-3 text-base font-semibold text-portal-ink">Eliminare la conversazione?</h3>
        <p className="mt-1 text-sm text-portal-ink-muted">
          Questa azione è definitiva. La conversazione e i suoi messaggi verranno rimossi.
        </p>
        <div className="mt-5 flex justify-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={deleting}
            className="rounded-full bg-portal-bg px-5 py-2 text-sm font-medium text-portal-ink-muted transition hover:text-portal-ink disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 rounded-full bg-portal-brand px-5 py-2 text-sm font-medium text-white transition hover:bg-portal-brand-hover disabled:opacity-50"
          >
            {deleting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />}
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryPanel({ history, onClose, onSelect, onDelete, activeId }) {
  const fmt = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ''; }
  };
  return (
    <div className="absolute inset-0 z-10 flex flex-col rounded-portal border border-portal-border bg-portal-bg/95 p-3 backdrop-blur">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-portal-ink">Le tue conversazioni</span>
        <button type="button" onClick={onClose} aria-label="Chiudi" className="text-portal-ink-muted hover:text-portal-ink">
          <X className="h-5 w-5" strokeWidth={1.75} />
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {history.status === 'loading' && (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-portal-ink-muted" /></div>
        )}
        {history.status === 'error' && (
          <p className="py-8 text-center text-sm text-portal-brand">Non riesco a caricare la cronologia.</p>
        )}
        {history.status === 'ready' && history.items.length === 0 && (
          <p className="py-8 text-center text-sm text-portal-ink-muted">Non hai ancora conversazioni.</p>
        )}
        {history.items.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onSelect(c.id)}
            className={clsx(
              'flex w-full items-center justify-between gap-2 rounded-2xl px-4 py-3 text-left transition',
              c.id === activeId ? 'bg-portal-brand/10' : 'bg-portal-card hover:bg-portal-brand/5'
            )}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium text-portal-ink">{c.title || 'Conversazione'}</span>
              <span className="block text-xs text-portal-ink-faint">{fmt(c.lastMessageAt)}</span>
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => onDelete(c.id, e)}
              aria-label="Elimina"
              className="shrink-0 rounded-full p-1.5 text-portal-ink-faint transition hover:bg-portal-brand/10 hover:text-portal-brand"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// Hide the follow-up actions block while streaming (emitted as `<<< ... >>>`).
function visibleText(content) {
  if (typeof content !== 'string') return content || '';
  const i = content.indexOf('<<<');
  return i === -1 ? content : content.slice(0, i).replace(/\s+$/, '');
}

function Bubble({ role, content, streaming }) {
  const isUser = role === 'user';
  return (
    <div className={clsx('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={clsx(
          'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm shadow-portal',
          isUser ? 'whitespace-pre-wrap bg-portal-brand text-white' : 'bg-portal-card text-portal-ink'
        )}
      >
        {isUser ? (
          content
        ) : (
          <div className="break-words [&_a]:text-portal-brand [&_a]:underline [&_strong]:font-semibold [&_em]:italic [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_h1]:mb-1 [&_h1]:mt-2 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mb-1 [&_h2]:mt-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:mb-1 [&_h3]:mt-2 [&_h3]:text-sm [&_h3]:font-semibold [&_blockquote]:my-2 [&_blockquote]:border-l-2 [&_blockquote]:border-portal-border [&_blockquote]:pl-3 [&_blockquote]:italic">
            {visibleText(content)
              ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{visibleText(content)}</ReactMarkdown>
              : (streaming ? <span className="text-portal-ink-faint">...</span> : '')}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice
// ---------------------------------------------------------------------------
function VoiceMode({ enabled }) {
  const voice = useRealtimeVoice();
  const { status, error, transcript, speaking, userSpeaking, muted, level, start, stop, toggleMute } = voice;
  const live = status === 'live' || status === 'connecting';

  useEffect(() => () => stop(), [stop]);

  if (!enabled) {
    return (
      <div className="rounded-portal bg-portal-card p-8 text-center shadow-portal">
        <p className="text-portal-ink-muted">La modalità vocale non è disponibile in questo momento.</p>
      </div>
    );
  }

  const label =
    status === 'connecting' ? 'Connessione...'
      : status === 'limited' ? 'Limite raggiunto'
        : status === 'error' ? 'Errore'
          : status === 'closed' ? 'Chiamata chiusa'
            : status !== 'live' ? 'Pronto'
              : speaking ? 'Sto parlando'
                : userSpeaking ? 'Ti ascolto'
                  : 'In ascolto';

  // The outer ring scales with the live voice amplitude; its colour follows who
  // is speaking (sage for the assistant, brand for the owner).
  const ringTone = speaking ? 'bg-portal-sage/30' : userSpeaking ? 'bg-portal-brand/25' : 'bg-portal-brand/10';
  const ringScale = 1 + (live ? level : 0) * 0.6;
  const ringOpacity = live ? 0.35 + level * 0.65 : 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6">
      {/* Orb: ORME mark in the centre, amplitude-reactive ring around it. */}
      <div className="flex flex-col items-center gap-3">
        <div className="relative flex h-44 w-44 items-center justify-center">
          {/* Reactive ring (amplitude). */}
          <span
            className={clsx('absolute h-36 w-36 rounded-full transition-[transform,opacity] duration-100 ease-out', ringTone)}
            style={{ transform: `scale(${ringScale})`, opacity: ringOpacity }}
          />
          {/* Soft static halo while connecting. */}
          {status === 'connecting' && (
            <span className="absolute h-36 w-36 animate-ping rounded-full bg-portal-brand/20" />
          )}
          {/* Centre disc with the logo. */}
          <div
            className={clsx(
              'relative z-10 flex h-28 w-28 items-center justify-center rounded-full bg-portal-card shadow-portal ring-2 transition-colors',
              status === 'error' ? 'ring-portal-brand/30'
                : speaking ? 'ring-portal-sage/50'
                  : userSpeaking ? 'ring-portal-brand/50'
                    : 'ring-portal-border'
            )}
          >
            <img src="/favicon.png" alt={env.appName} className="h-12 w-12 object-contain" />
          </div>
        </div>
        <span className="text-sm font-medium text-portal-ink-muted">{label}</span>
      </div>

      {error && <p className="text-center text-sm text-portal-brand">{error}</p>}

      {/* Live transcript (kept light) */}
      {transcript.length > 0 && (
        <div className="max-h-40 w-full max-w-md space-y-2 overflow-y-auto">
          {transcript.filter((t) => t.text).map((t) => (
            <Bubble key={t.id} role={t.role} content={t.text} />
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3">
        {!live ? (
          <button
            type="button"
            onClick={() => start()}
            className="inline-flex items-center gap-2 rounded-full bg-portal-brand px-6 py-3 text-sm font-medium text-white hover:bg-portal-brand-hover"
          >
            <Phone className="h-4 w-4" strokeWidth={1.75} />
            Inizia a parlare
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? 'Riattiva microfono' : 'Silenzia microfono'}
              className={clsx(
                'inline-flex h-12 w-12 items-center justify-center rounded-full border transition',
                muted ? 'border-portal-brand bg-portal-brand/10 text-portal-brand' : 'border-portal-border bg-portal-card text-portal-ink-muted hover:text-portal-ink'
              )}
            >
              {muted ? <MicOff className="h-5 w-5" strokeWidth={1.75} /> : <Mic className="h-5 w-5" strokeWidth={1.75} />}
            </button>
            <button
              type="button"
              onClick={stop}
              aria-label="Termina"
              className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-portal-brand text-white hover:bg-portal-brand-hover"
            >
              <PhoneOff className="h-5 w-5" strokeWidth={1.75} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
